import { useState, useEffect } from "react"
import { useGetWalletsQuery, useGetWalletStatsQuery, useRequestWalletAdjustmentOTPMutation, useAdjustWalletBalanceMutation, useToggleWalletStatusMutation } from "../features/admin/adminApi"
import { Wallet, Search, DollarSign, Users, Building2, Edit, Power, Shield } from "lucide-react"
import Modal from "../components/Modal"

export default function WalletPage() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [ownerTypeFilter, setOwnerTypeFilter] = useState("")
  const [minBalance, setMinBalance] = useState("")
  const [maxBalance, setMaxBalance] = useState("")

  const [showAdjustModal, setShowAdjustModal] = useState(false)
  const [selectedWallet, setSelectedWallet] = useState<any>(null)
  const [adjustAmount, setAdjustAmount] = useState("")
  const [adjustReason, setAdjustReason] = useState("")
  const [otpCode, setOtpCode] = useState("")
  const [showOtpStep, setShowOtpStep] = useState(false)
  const [otpCountdown, setOtpCountdown] = useState(0)
  const [isRequestingOtp, setIsRequestingOtp] = useState(false)

  const { data, isLoading, error } = useGetWalletsQuery({
    page,
    search: debouncedSearch,
    ownerType: ownerTypeFilter,
    minBalance: minBalance ? parseFloat(minBalance) : undefined,
    maxBalance: maxBalance ? parseFloat(maxBalance) : undefined,
  })

  const { data: stats } = useGetWalletStatsQuery()
  const [requestOTP] = useRequestWalletAdjustmentOTPMutation()
  const [adjustBalance] = useAdjustWalletBalanceMutation()
  const [toggleStatus] = useToggleWalletStatusMutation()

  // Countdown timer for OTP
  useEffect(() => {
    if (otpCountdown > 0) {
      const timer = setTimeout(() => setOtpCountdown(otpCountdown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [otpCountdown])

  const handleSearch = (value: string) => {
    setSearch(value)
    const timer = setTimeout(() => setDebouncedSearch(value), 500)
    return () => clearTimeout(timer)
  }

  const handleOpenAdjustModal = (wallet: any) => {
    setSelectedWallet(wallet)
    setAdjustAmount("")
    setAdjustReason("")
    setOtpCode("")
    setShowOtpStep(false)
    setOtpCountdown(0)
    setShowAdjustModal(true)
  }

  const handleRequestOTP = async () => {
    if (!selectedWallet || !adjustAmount || !adjustReason) {
      alert("Please fill in amount and reason first")
      return
    }

    const amount = parseFloat(adjustAmount)
    if (isNaN(amount) || amount === 0) {
      alert("Please enter a valid non-zero amount")
      return
    }

    setIsRequestingOtp(true)
    try {
      const result = await requestOTP({
        uid: selectedWallet.id,
        amount,
        reason: adjustReason,
      }).unwrap()

      alert(result.message || "OTP sent to your email")
      setShowOtpStep(true)
      setOtpCountdown(result.expiresIn || 600)
      setOtpCode("")
    } catch (err: any) {
      alert(err?.data?.message || "Failed to send OTP")
    } finally {
      setIsRequestingOtp(false)
    }
  }

  const handleAdjustBalance = async () => {
    if (!selectedWallet || !adjustAmount || !adjustReason || !otpCode) {
      alert("Please fill in all fields including OTP")
      return
    }

    const amount = parseFloat(adjustAmount)
    if (isNaN(amount) || amount === 0) {
      alert("Please enter a valid non-zero amount")
      return
    }

    if (otpCode.length !== 6) {
      alert("OTP must be 6 digits")
      return
    }

    try {
      await adjustBalance({
        uid: selectedWallet.id,
        amount,
        reason: adjustReason,
        otp: otpCode,
      }).unwrap()
      alert("Wallet balance adjusted successfully")
      setShowAdjustModal(false)
      setShowOtpStep(false)
      setOtpCode("")
    } catch (err: any) {
      alert(err?.data?.message || "Failed to adjust wallet balance")
    }
  }

  const handleToggleStatus = async (walletId: string, currentStatus: boolean) => {
    const action = currentStatus ? "deactivate" : "activate"
    if (!confirm(`Are you sure you want to ${action} this wallet?`)) return

    try {
      await toggleStatus({ uid: walletId, isActive: !currentStatus }).unwrap()
      alert(`Wallet ${action}d successfully`)
    } catch (err: any) {
      alert(err?.data?.message || `Failed to ${action} wallet`)
    }
  }

  const formatCurrency = (amount: number, currency: string = "NGN") => {
    const symbol = currency === "NGN" ? "₦" : currency
    return `${symbol}${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  const getOwnerTypeBadge = (type: string) => {
    return type === "user"
      ? "bg-blue-100 text-blue-800"
      : "bg-purple-100 text-purple-800"
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Wallet size={28} />
          Wallet Management
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Monitor wallet balances, adjust funds, and track platform liquidity
        </p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Balance</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {formatCurrency(stats.totalBalance)}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <DollarSign size={24} className="text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Wallets</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {stats.totalWallets.toLocaleString()}
                </p>
                <p className="text-xs text-green-600 mt-1">{stats.activeWallets} active</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Wallet size={24} className="text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">User Wallets</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">
                  {stats.userWallets.toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users size={24} className="text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Business Wallets</p>
                <p className="text-2xl font-bold text-purple-600 mt-1">
                  {stats.businessWallets.toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <Building2 size={24} className="text-purple-600" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <select
            value={ownerTypeFilter}
            onChange={(e) => setOwnerTypeFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Types</option>
            <option value="user">Users</option>
            <option value="business">Businesses</option>
          </select>

          <input
            type="number"
            placeholder="Min Balance"
            value={minBalance}
            onChange={(e) => setMinBalance(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          <input
            type="number"
            placeholder="Max Balance"
            value={maxBalance}
            onChange={(e) => setMaxBalance(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          <button
            onClick={() => {
              setOwnerTypeFilter("")
              setMinBalance("")
              setMaxBalance("")
              setSearch("")
              setDebouncedSearch("")
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Summary */}
      {data && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-900">
            Showing <strong>{data.wallets.length}</strong> of <strong>{data.total}</strong> wallets
          </p>
        </div>
      )}

      {/* Wallets Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center text-gray-500">Loading wallets...</div>
        ) : error ? (
          <div className="p-12 text-center text-red-600">Failed to load wallets</div>
        ) : !data?.wallets.length ? (
          <div className="p-12 text-center text-gray-500">
            <Wallet size={48} className="mx-auto mb-3 text-gray-300" />
            <p>No wallets found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Owner</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Balance</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Currency</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Created</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data.wallets.map((wallet) => (
                  <tr key={wallet.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{wallet.ownerName || "N/A"}</p>
                        {wallet.ownerEmail && (
                          <p className="text-xs text-gray-500 mt-0.5">{wallet.ownerEmail}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium capitalize ${getOwnerTypeBadge(wallet.ownerType)}`}>
                        {wallet.ownerType}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-bold text-gray-900">{formatCurrency(wallet.balance, wallet.currency)}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{wallet.currency}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${wallet.isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                        {wallet.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {new Date(wallet.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleOpenAdjustModal(wallet)}
                          className="p-1.5 hover:bg-blue-50 rounded text-blue-600"
                          title="Adjust Balance"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleToggleStatus(wallet.id, wallet.isActive)}
                          className={`p-1.5 hover:bg-gray-100 rounded ${wallet.isActive ? "text-red-600" : "text-green-600"}`}
                          title={wallet.isActive ? "Deactivate" : "Activate"}
                        >
                          <Power size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {data && data.totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Page {page} of {data.totalPages}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
              disabled={page === data.totalPages}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Adjust Balance Modal with 2FA */}
      {showAdjustModal && selectedWallet && (
        <Modal
          title="Adjust Wallet Balance"
          onClose={() => {
            setShowAdjustModal(false)
            setShowOtpStep(false)
            setOtpCode("")
          }}
        >
          <div className="space-y-4">
            {/* Security Notice */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-2">
              <Shield className="text-yellow-600 flex-shrink-0 mt-0.5" size={18} />
              <p className="text-sm text-yellow-800">
                <strong>Security:</strong> This action requires 2FA verification. An OTP will be sent to your email.
              </p>
            </div>

            <div>
              <p className="text-sm text-gray-600">
                <strong>Owner:</strong> {selectedWallet.ownerName}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Current Balance:</strong> {formatCurrency(selectedWallet.balance, selectedWallet.currency)}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount (use negative for debit)
              </label>
              <input
                type="number"
                step="0.01"
                value={adjustAmount}
                onChange={(e) => setAdjustAmount(e.target.value)}
                placeholder="e.g., 1000 or -500"
                disabled={showOtpStep}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">
                Positive number to credit, negative to debit
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason (required)
              </label>
              <textarea
                value={adjustReason}
                onChange={(e) => setAdjustReason(e.target.value)}
                placeholder="Explain the reason for this adjustment..."
                rows={3}
                disabled={showOtpStep}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            {adjustAmount && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-900">
                  <strong>Preview:</strong> {parseFloat(adjustAmount) > 0 ? "Credit" : "Debit"} of{" "}
                  <strong>{formatCurrency(Math.abs(parseFloat(adjustAmount) || 0), selectedWallet.currency)}</strong>
                </p>
                <p className="text-sm text-blue-900 mt-1">
                  New Balance:{" "}
                  <strong>
                    {formatCurrency(
                      selectedWallet.balance + (parseFloat(adjustAmount) || 0),
                      selectedWallet.currency
                    )}
                  </strong>
                </p>
              </div>
            )}

            {/* OTP Step */}
            {showOtpStep && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-green-900">
                    ✓ OTP sent to your email
                  </p>
                  {otpCountdown > 0 && (
                    <span className="text-xs font-mono text-green-700 bg-green-100 px-2 py-1 rounded">
                      {Math.floor(otpCountdown / 60)}:{String(otpCountdown % 60).padStart(2, '0')}
                    </span>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Enter 6-digit OTP
                  </label>
                  <input
                    type="text"
                    maxLength={6}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 font-mono text-lg tracking-widest text-center"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Check your email for the verification code
                  </p>
                </div>

                <button
                  onClick={handleRequestOTP}
                  disabled={isRequestingOtp || otpCountdown > 540}
                  className="text-sm text-blue-600 hover:text-blue-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  {otpCountdown > 540 ? 'Wait to resend...' : 'Resend OTP'}
                </button>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 mt-6">
              {!showOtpStep ? (
                <button
                  onClick={handleRequestOTP}
                  disabled={isRequestingOtp || !adjustAmount || !adjustReason}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isRequestingOtp ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Sending OTP...
                    </>
                  ) : (
                    <>
                      <Shield size={18} />
                      Request OTP
                    </>
                  )}
                </button>
              ) : (
                <button
                  onClick={handleAdjustBalance}
                  disabled={otpCode.length !== 6}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Verify & Adjust Balance
                </button>
              )}
              <button
                onClick={() => {
                  setShowAdjustModal(false)
                  setShowOtpStep(false)
                  setOtpCode("")
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
